import axios, { AxiosInstance, AxiosResponse } from "axios";
import Cookies from "js-cookie";
import toast from "react-hot-toast";
import config from "../config/env";
import {
  BatchStatus,
  getBatchStatusDisplayText,
  getBatchStatusClass,
} from "../types/batchStatus";
import { tokenRefreshService } from "./tokenRefreshService";

export interface BatchHistoryItem {
  id: number;
  userId: number;
  executionDate: string;
  errorMsg: string | null;
  status: BatchStatus;
  totalItems: number;
  isDetailViewable: boolean;
  documentStats: {
    successDocuments: number;
    failedDocuments: number;
    pendingDocuments: number;
    totalDocuments: number;
  };
}

export interface BatchHistoryItemApi {
  id: number;
  userId: number;
  executionDate: string;
  errorMsg: string | null;
  status: string; // API returns string status
  totalItems: number;
  isDetailViewable: boolean;
  documentStats: {
    successDocuments: number;
    failedDocuments: number;
    pendingDocuments: number;
    totalDocuments: number;
  };
}

export interface BatchHistoryResponse {
  success: boolean;
  data: BatchHistoryItem[];
  pagination: {
    page: number;
    perPage: number;
    total: string;
    totalPages: number;
  };
  message: string;
}

export interface BatchHistoryApiResponse {
  success: boolean;
  data: BatchHistoryItemApi[];
  pagination: {
    page: number;
    perPage: number;
    total: string;
    totalPages: number;
  };
  message: string;
}

export interface BatchHistoryParams {
  startDate?: string;
  endDate?: string;
  page?: number;
  perPage?: number;
  status?: BatchStatus;
}

class BatchHistoryService {
  private client: AxiosInstance;

  constructor() {
    const baseURL = config.apiUrl;

    this.client = axios.create({
      baseURL: `${baseURL}`,
      timeout: 15000,
      headers: {
        "Content-Type": "application/json",
      },
    });

    // Request interceptor to add auth token
    this.client.interceptors.request.use(
      config => {
        const token = Cookies.get("access_token");
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
      },
      error => {
        console.error("Batch history request error:", error);
        return Promise.reject(error);
      }
    );

    // Response interceptor for error handling
    this.client.interceptors.response.use(
      response => response,
      error => {
        console.error(
          "Batch history response error:",
          error.response?.data || error.message
        );

        // Handle timeout errors
        if (
          error.code === "ECONNABORTED" ||
          error.message.includes("timeout")
        ) {
          toast.error(
            "リクエストがタイムアウトしました。しばらく待ってから再試行してください。"
          );
          return Promise.reject({
            success: false,
            message:
              "リクエストがタイムアウトしました。しばらく待ってから再試行してください。",
            errors: [
              {
                field: "general",
                message:
                  "ネットワーク接続が遅いか、サーバーが応答していません。",
              },
            ],
            isTimeout: true,
          });
        }

        // Don't handle 401 errors - let token refresh interceptor handle them
        if (error.response?.status === 401) {
          return Promise.reject(error);
        }

        return Promise.reject(error);
      }
    );

    // Setup automatic token refresh
    tokenRefreshService.setupAxiosInterceptor(this.client);
  }

  /**
   * Map API status string to BatchStatus enum
   */
  private mapApiStatusToBatchStatus(apiStatus: string): BatchStatus {
    switch (apiStatus.toUpperCase()) {
      case "SUCCESS":
        return BatchStatus.SUCCESS;
      case "ERROR":
      case "FAILED":
        return BatchStatus.FAILED;
      case "PENDING":
        return BatchStatus.PENDING;
      default:
        return BatchStatus.FAILED; // Default to FAILED for unknown statuses
    }
  }

  /**
   * Convert API response item to internal format
   */
  private convertApiItemToBatchItem(
    apiItem: BatchHistoryItemApi
  ): BatchHistoryItem {
    return {
      ...apiItem,
      status: this.mapApiStatusToBatchStatus(apiItem.status),
    };
  }

  /**
   * Get batch history with filters
   */
  async getBatchHistory(
    params: BatchHistoryParams = {}
  ): Promise<BatchHistoryResponse> {
    try {
      const queryParams = new URLSearchParams();

      if (params.startDate) {
        queryParams.append("startDate", params.startDate);
      }
      if (params.endDate) {
        queryParams.append("endDate", params.endDate);
      }
      if (params.page) {
        queryParams.append("page", params.page.toString());
      }
      if (params.perPage) {
        queryParams.append("perPage", params.perPage.toString());
      }
      if (params.status && params.status !== BatchStatus.ALL) {
        queryParams.append("status", params.status);
      }

      const url = `/batch_history${queryParams.toString() ? `?${queryParams.toString()}` : ""}`;

      const response: AxiosResponse<BatchHistoryApiResponse> =
        await this.client.get(url);

      // Convert API response to internal format
      const convertedData: BatchHistoryItem[] = response.data.data.map(item =>
        this.convertApiItemToBatchItem(item)
      );

      return {
        success: response.data.success,
        data: convertedData,
        pagination: response.data.pagination,
        message: response.data.message,
      };
    } catch (error: any) {
      console.error("Error fetching batch history:", error);
      throw new Error(
        error.response?.data?.message || "バッチ履歴の取得に失敗しました"
      );
    }
  }

  /**
   * Format date for display
   */
  formatExecutionDate(dateString: string): string {
    const date = new Date(dateString);
    return date.toLocaleString("ja-JP", {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
      hour12: false,
    });
  }

  /**
   * Get status display text in Japanese
   */
  getStatusDisplayText(status: BatchStatus): string {
    return getBatchStatusDisplayText(status);
  }

  /**
   * Get status CSS class
   */
  getStatusClass(status: BatchStatus): string {
    return getBatchStatusClass(status);
  }

  /**
   * Trigger manual import of all unprocessed documents
   */
  async triggerImport(): Promise<{
    success: boolean;
    data?: { totalRecords: number; sentRecords: number };
    message?: string;
    error?: string;
  }> {
    try {
      const response = await this.client.post("/batch_history/import");
      return response.data;
    } catch (error: any) {
      console.error("Error triggering import:", error);
      const msg =
        error.response?.data?.message || "取込処理の実行に失敗しました";
      throw new Error(msg);
    }
  }

  /**
   * Format document stats for display
   */
  formatDocumentStats(stats: BatchHistoryItem["documentStats"]): {
    total: number;
    success: number;
    failed: number;
    // pending: number;
  } {
    const successDocuments = stats.successDocuments;
    const failedDocuments = stats.failedDocuments;
    const totalDocuments = stats.totalDocuments;
    return {
      total: totalDocuments,
      success: successDocuments,
      failed: failedDocuments,
      // pending: pendingDocuments
    };
  }
}

// Export singleton instance
export const batchHistoryService = new BatchHistoryService();
export default batchHistoryService;
